import React, { useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Download } from 'lucide-react';

export default function InvoicePDFButton({ invoice }) {
  const printRef = useRef();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadPdf = async (e) => {
    e.stopPropagation();
    setIsGenerating(true);
    const element = printRef.current;
    
    element.style.display = 'block';

    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // wait for rendering

      const canvas = await html2canvas(element, { 
        scale: 2, 
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice_${invoice.id}_SVT.pdf`);
    } catch(err) {
      console.error(err);
    } finally {
      element.style.display = 'none';
      setIsGenerating(false);
    }
  };

  return (
    <>
      <button 
        onClick={handleDownloadPdf} 
        disabled={isGenerating}
        className="text-primary hover:text-gray-900 transition-colors"
        title="Download Invoice"
      >
        {isGenerating ? <span className="animate-spin inline-block border-2 border-primary border-t-transparent rounded-full w-4 h-4"></span> : <Download size={18}/>}
      </button>

      {/* Hidden PDF Template */}
      <div style={{ position: 'absolute', left: '-9999px', top: '0', pointerEvents: 'none' }}>
        <div 
          ref={printRef} 
          style={{ 
            display: 'none', 
            width: '210mm', 
            minHeight: '297mm', 
            padding: '20mm', 
            backgroundColor: '#ffffff', 
            color: '#000000', 
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', 
            boxSizing: 'border-box'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '3px solid #D4AF37', paddingBottom: '20px', marginBottom: '30px' }}>
            <div style={{ width: '60%' }}>
              {/* Logo Placeholder */}
              <div style={{ 
                width: '180px', height: '60px', 
                backgroundColor: '#f0f0f0', border: '1px dashed #ccc', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                marginBottom: '15px', color: '#999', fontSize: '12px', fontWeight: 'bold'
              }}>
                [YOUR COMPANY LOGO HERE]
              </div>
              <h1 style={{ fontSize: '28px', fontWeight: '900', margin: '0 0 5px 0', letterSpacing: '1px', color: '#05140D', textTransform: 'uppercase' }}>
                SVT GOLDEN FITNESS
              </h1>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '14px', color: '#333' }}>
                SVT AA VENTURES PVT. LTD.
              </p>
              <p style={{ margin: '0', fontSize: '12px', color: '#555', lineHeight: '1.4' }}>
                No 26/7, 2nd floor, Jawaharlal Nehru rd, BEML layout 3rd stage,<br/>
                Rajarajeshwari Nagar, Bengaluru - 560098.<br/>
                Mob: 81237 65171<br/>
                Email: accounts@svtgolden.com<br/>
                GSTIN: [YOUR GST NUMBER]
              </p>
            </div>
            <div style={{ width: '35%', textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <h2 style={{ fontSize: '32px', margin: '0 0 15px 0', color: '#D4AF37', textTransform: 'uppercase' }}>INVOICE</h2>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 0', color: '#555', fontWeight: 'bold' }}>Invoice No:</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 'bold' }}>{invoice.id}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 0', color: '#555', fontWeight: 'bold' }}>Date:</td>
                    <td style={{ padding: '4px 0', textAlign: 'right' }}>{invoice.date}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 0', color: '#555', fontWeight: 'bold' }}>Status:</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', color: invoice.status === 'Paid' ? '#4ADE80' : '#F87171', fontWeight: 'bold' }}>{invoice.status}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Bill To */}
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{ fontSize: '14px', color: '#D4AF37', borderBottom: '1px solid #eee', paddingBottom: '5px', marginBottom: '10px', textTransform: 'uppercase' }}>Bill To</h3>
            <p style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: 'bold' }}>{invoice.member}</p>
            <p style={{ margin: '0', fontSize: '12px', color: '#555' }}>Gym Member / Customer</p>
          </div>

          {/* Items Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
            <thead>
              <tr style={{ backgroundColor: '#05140D', color: '#D4AF37' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase' }}>Description / Type</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', textTransform: 'uppercase' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '15px 12px', borderBottom: '1px solid #eee', fontSize: '14px', color: '#333' }}>
                  {invoice.type}
                </td>
                <td style={{ padding: '15px 12px', borderBottom: '1px solid #eee', fontSize: '14px', textAlign: 'right', fontWeight: 'bold' }}>
                  ₹{invoice.amount.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '50px' }}>
            <div style={{ width: '40%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '8px 12px', color: '#555', fontSize: '12px' }}>Subtotal</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px' }}>₹{invoice.amount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 12px', color: '#555', fontSize: '12px', borderBottom: '1px solid #ccc' }}>Tax (Included/Exempt)</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', borderBottom: '1px solid #ccc' }}>₹0</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', fontWeight: 'bold', fontSize: '16px', color: '#05140D' }}>Total</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: '#D4AF37' }}>₹{invoice.amount.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Notes */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: '20px', fontSize: '10px', color: '#777', textAlign: 'center' }}>
            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#333' }}>Thank you for your business!</p>
            <p style={{ margin: '0 0 5px 0' }}>For any queries regarding this invoice, please contact accounts@svtgolden.com</p>
            <p style={{ margin: '0' }}>Payment is due upon receipt. This is a computer-generated invoice and does not require a physical signature.</p>
          </div>
        </div>
      </div>
    </>
  );
}
